import {
	Avatar,
	Box,
	Button,
	CircularProgress,
	FormLabel,
	Stack,
	styled,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import {
	useGlobalDispatch,
	useGlobalState,
} from '../stateProviders/GlobalStateProvider';
import { AppUserUpdateParams } from '../types/api/AppUser';

export const UserSettings = () => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const globalDispatch = useGlobalDispatch();
	const globalState = useGlobalState();
	const api = useApi();

	const [profilePicture, setProfilePicture] = useState(
		globalState.appUser?.profilePicture || '/avatar.jpg'
	);
	const [profilePictureFile, setProfilePictureFile] = useState<File | null>(
		null
	);
	const [firstName, setFirstName] = useState(
		globalState.appUser?.firstName || ''
	);
	const [lastName, setLastName] = useState(globalState.appUser?.lastName || '');
	const [phoneNumber, setPhoneNumber] = useState(
		globalState.appUser?.phoneNumber || ''
	);
	const [isSaving, setIsSaving] = useState(false);

	const discardChanges = () => {
		setProfilePicture(globalState.appUser?.profilePicture || '/avatar.jpg');
		setProfilePictureFile(null);
		setFirstName(globalState.appUser?.firstName || '');
		setLastName(globalState.appUser?.lastName || '');
		setPhoneNumber(globalState.appUser?.phoneNumber || '');
	};

	const updateProfile = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!globalState.appUser?._id) return;
		try {
			setIsSaving(true);
			const updateParams: AppUserUpdateParams = {
				firstName,
				lastName,
				phoneNumber,
			};
			if (profilePicture) {
				updateParams.profilePicture = profilePictureFile;
			}
			console.log('📤 Updating with params:', updateParams);

			await api.appUser.updateMe(updateParams);

			// Fetch the updated user
			const updatedUser = await api.appUser.getMe();
			setProfilePicture(updatedUser.profilePicture);
			console.log(
				'✅ Updated user profile picture:',
				updatedUser.profilePicture
			);

			globalDispatch({ appUser: updatedUser });
		} catch (error) {
			console.error(error);
		} finally {
			setIsSaving(false);
			setProfilePictureFile(null);
		}
	};

	const VisuallyHiddenInput = styled('input')({
		clip: 'rect(0 0 0 0)',
		clipPath: 'inset(50%)',
		height: 1,
		overflow: 'hidden',
		position: 'absolute',
		bottom: 0,
		left: 0,
		whiteSpace: 'nowrap',
		width: 1,
	});

	return (
		<Box
			display="flex"
			flexDirection="column"
			maxWidth={{ xs: '100%', sm: 600, md: 800, lg: 1000 }}
			component="form"
			rowGap={{ xs: 3, sm: 4 }}
			onSubmit={updateProfile}
		>
			<Stack
				direction={isSmallScreen ? 'column' : 'row'}
				spacing={4}
				alignItems={isSmallScreen ? 'flex-start' : 'center'}
			>
				<Avatar
					src={
						profilePictureFile
							? URL.createObjectURL(profilePictureFile)
							: profilePicture
					}
					alt="Profile Photo"
					sx={{ width: 112, height: 112 }}
					slotProps={{
						img: {
							referrerPolicy: 'no-referrer',
							style: { objectFit: 'contain' },
						},
					}}
				/>
				<Stack
					direction="column"
					spacing={1}
					flexWrap="wrap"
					alignItems="start"
				>
					<Button
						component="label"
						role={undefined}
						variant="outlined"
						color="primary"
						tabIndex={-1}
					>
						Upload Avatar
						<VisuallyHiddenInput
							type="file"
							accept=".jpg,.jpeg,.png,.webp,.svg,.gif"
							onChange={(event) =>
								setProfilePictureFile(event.target.files?.[0] || null)
							}
						/>
					</Button>
					<Button
						color="error"
						variant="text"
						size="small"
						disabled={isSaving || profilePicture === '/avatar.jpg'}
						onClick={discardChanges}
					>
						Cancel
					</Button>
					{profilePictureFile && (
						<Typography
							sx={{ fontStyle: 'italic', color: 'gray.600', width: '100%' }}
							component="span"
						>
							{profilePictureFile.name}
						</Typography>
					)}
				</Stack>
			</Stack>
			<Box
				display="grid"
				gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
				gap={3}
			>
				<Box>
					<FormLabel component="legend" sx={{ mb: 1 }}>
						First Name
					</FormLabel>
					<TextField
						fullWidth
						name="firstName"
						value={firstName}
						onChange={(e) => setFirstName(e.target.value)}
						disabled={isSaving}
						data-testid="first-name"
					/>
				</Box>
				<Box>
					<FormLabel component="legend" sx={{ mb: 1 }}>
						Last Name
					</FormLabel>
					<TextField
						fullWidth
						name="lastName"
						value={lastName}
						onChange={(e) => setLastName(e.target.value)}
						disabled={isSaving}
						data-testid="last-name"
					/>
				</Box>
				<Box>
					<FormLabel component="legend" sx={{ mb: 1 }}>
						Email
					</FormLabel>
					<TextField
						fullWidth
						name="email"
						value={globalState.appUser?.email}
						disabled
					/>
				</Box>
				<Box>
					<FormLabel component="legend" sx={{ mb: 1 }}>
						Phone Number
					</FormLabel>
					<TextField
						fullWidth
						name="phoneNumber"
						type="tel"
						value={phoneNumber}
						onChange={(e) => setPhoneNumber(e.target.value)}
						disabled={isSaving}
					/>
				</Box>
			</Box>
			<Box height={40} display="flex" fontWeight={600}>
				<Button
					type="submit"
					variant="contained"
					color="success"
					disabled={!firstName.trim() || !lastName.trim() || isSaving}
					data-testid="save"
				>
					{isSaving ? (
						<Box display="flex" alignItems="center" gap={1}>
							<CircularProgress size={18} color="success" />
							<Typography variant="xs" component="span" fontWeight={600}>
								Saving...
							</Typography>
						</Box>
					) : (
						'Save'
					)}
				</Button>
			</Box>
		</Box>
	);
};
